// src/components/hooks/useRedirectAfterLogin.ts

import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { supabase } from '@/lib/supabaseClient';
import { rutaInicioPanel } from '@/components/utils/rutasPanel';

type RedirectContext = 'login' | 'clientBooking' | 'afterClientRegister';

export const useRedirectAfterLogin = () => {
  const navigate = useNavigate();

  const redirect = useCallback(
    async (userId: string, context: RedirectContext = 'login') => {
      if (!userId) {
        Sentry.captureMessage(
          '[useRedirectAfterLogin] userId vacío',
          'warning'
        );
        return { ok: false, error: 'UserId vacío' };
      }

      /* =========================================================
         🔹 ClientBooking: no redirige
      ========================================================= */
      if (context === 'clientBooking') {
        return { ok: true, redirect: null };
      }

      /* =========================================================
         ✅ REGISTRO CLIENTE FINALIZADO
      ========================================================= */
      if (context === 'afterClientRegister') {
        localStorage.removeItem('id_perfil');
        localStorage.removeItem('id_empresa');

        const { data: userData } = await supabase.auth.getUser();
        const email = userData.user?.email || '';

        localStorage.setItem('id_usuario', userId);
        localStorage.setItem('email', email);

        navigate('/panel-cliente');
        return { ok: true, redirect: '/panel-cliente' };
      }

      /* =========================================================
         🔹 LOGIN NORMAL
      ========================================================= */
      localStorage.removeItem('id_perfil');
      localStorage.removeItem('id_empresa');

      const { data: userData } = await supabase.auth.getUser();
      const email = userData.user?.email || '';

      localStorage.setItem('id_usuario', userId);
      localStorage.setItem('email', email);

      const { data: perfiles, error } = await supabase
        .from('vista_usuarios_detalle')
        .select('id_perfil, id_empresa, ruta_panel, inicio_automatico')
        .eq('id_usuario', userId);

      if (error) {
        Sentry.captureException(error, { extra: { userId, context } });
      }

      if (error || !perfiles || perfiles.length === 0) {
        navigate('/profiles');
        return { ok: true, redirect: '/profiles' };
      }

      const perfilAuto = perfiles.find((p) => p.inicio_automatico);

      /* =========================================================
         🔹 SOLO 1 PERFIL
      ========================================================= */
      if (perfiles.length === 1) {
        const p = perfiles[0];

        localStorage.setItem('id_perfil', p.id_perfil || '');
        localStorage.setItem('id_empresa', p.id_empresa || '');

        const destino = rutaInicioPanel(p.ruta_panel);
        navigate(destino);
        return { ok: true, redirect: destino };
      }

      /* =========================================================
         🔹 PERFIL AUTOMÁTICO
      ========================================================= */
      if (perfilAuto) {
        localStorage.setItem('id_perfil', perfilAuto.id_perfil || '');
        localStorage.setItem('id_empresa', perfilAuto.id_empresa || '');

        const destino = rutaInicioPanel(perfilAuto.ruta_panel);
        navigate(destino);
        return { ok: true, redirect: destino };
      }

      /* =========================================================
         🔹 MÚLTIPLES PERFILES → elegir uno
      ========================================================= */
      navigate('/profiles');
      return { ok: true, redirect: '/profiles' };
    },
    [navigate]
  );

  return { redirect };
};